import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSwotWizard } from "@/hooks/queries/dashboard/usePredictiveDashboard";
import { Button, Textarea, Skeleton } from "@/components/ui";
import { Bot, Target, Zap, ShieldAlert, Flag, ChevronRight, Check } from "lucide-react";
import { PremiumPaywall } from "@/components/ui/PremiumPaywall";

const WIZARD_QUESTIONS = [
    {
        id: "focus",
        title: "Foco Principal",
        question: "Qual é o principal foco estratégico da clínica neste trimestre?",
        description: "Pense em metas de vendas, retenção, ou lançamento de novos procedimentos.",
        placeholder: "Ex: Quero aumentar as vendas de preenchimento labial em 30%..."
    },
    {
        id: "challenge",
        title: "Maior Desafio",
        question: "Qual o maior desafio operacional ou comercial que você enfrenta hoje?",
        description: "Gargalos na equipe, pacientes não comparecendo (no-show), dificuldade de conversão...",
        placeholder: "Ex: Minha taxa de faltas está muito alta nas segundas-feiras..."
    },
    {
        id: "advantage",
        title: "Vantagem Competitiva",
        question: "Qual o seu maior diferencial perante os concorrentes locais?",
        description: "O que faz o paciente escolher você e não a clínica vizinha?",
        placeholder: "Ex: Temos o laser mais moderno da região e atendimento boutique..."
    }
];

// P-06: the wizard only ever renders inside Meu Negocio, and a page inside
// the shell does not own the viewport height. The standalone wrapper it drew
// when `embedded` was false was both unreachable and a second scrollbar, so
// the prop went with it.
export function SwotWizard() {
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const [currentStep, setCurrentStep] = useState(0);

    // We can still use the same query, we'll pass the answers or context when we have actual backend processing
    const { data, mutate, isLoading, error } = useSwotWizard();

    const swotData = data?.data;
    
    const quotaExceeded = error && (error as any)?.response?.status === 402;
    const quotaMessage = (error as any)?.response?.data?.message;

    const currentQuestion = WIZARD_QUESTIONS[currentStep];
    const isLastStep = currentStep === WIZARD_QUESTIONS.length - 1;
    const canProceed = (answers[currentQuestion.id] || "").trim().length > 3;
    const showWizard = !swotData && !isLoading;

    const handleNext = () => {
        if (currentStep < WIZARD_QUESTIONS.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            handleGenerate();
        }
    };

    const handleGenerate = () => {
        // Pass the filled answers to the backend mutation
        mutate(answers);
    };

    if (quotaExceeded) {
        return (
            <div className="p-6 flex items-center justify-center">
                <div className="max-w-3xl w-full">
                    <PremiumPaywall message={quotaMessage} title="Inteligência Estratégica Esgotada" />
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <motion.div
                className="space-y-8 max-w-7xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <div className="flex items-center gap-4 border-b border-app-border-subtle pb-6">
                    <div className="bg-brand p-3 rounded-2xl shadow-lg">
                        <Target className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-app-fg">
                            Planejamento Estratégico AI (SWOT)
                        </h1>
                        <p className="text-app-fg-muted mt-1">
                            O Dr. Insights conduz uma entrevista interativa e cruza as respostas com os dados da clínica para gerar uma matriz acionável.
                        </p>
                    </div>
                </div>

                {/* Dynamic Wizard Steps */}
                <AnimatePresence mode="wait">
                    {showWizard && (
                        <motion.div
                            key="wizard-form"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-xl border border-app-border-subtle overflow-hidden"
                        >
                            <div className="flex flex-col md:flex-row">
                                {/* Sidebar Progress */}
                                <div className="w-full md:w-[35%] bg-app-surface-muted border-r border-app-border-subtle p-8 flex flex-col justify-center">
                                    <h3 className="font-bold text-app-fg mb-8 flex items-center gap-2 text-lg">
                                        <Bot className="w-6 h-6 text-brand" />
                                        Entrevista IA
                                    </h3>
                                    <div className="space-y-8">
                                        {WIZARD_QUESTIONS.map((q, idx) => {
                                            const isCompleted = currentStep > idx;
                                            const isActive = currentStep === idx;
                                            return (
                                                <div key={q.id} className={`flex items-start gap-4 transition-all duration-300 ${isActive ? 'opacity-100 scale-105' : isCompleted ? 'opacity-70' : 'opacity-40'}`}>
                                                    <div className={`mt-0.5 flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold shadow-sm ${isCompleted ? 'bg-brand text-white' : isActive ? 'bg-app-fg-strong text-white' : 'bg-app-border text-app-fg-muted border border-app-border'}`}>
                                                        {isCompleted ? <Check className="w-4 h-4" /> : idx + 1}
                                                    </div>
                                                    <div>
                                                        <p className={`font-semibold text-base ${isActive ? 'text-app-fg-strong' : 'text-app-fg-muted'}`}>{q.title}</p>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>

                                {/* Active Question Panel */}
                                <div className="w-full md:w-[65%] p-8 lg:p-12 flex flex-col justify-center min-h-[400px]">
                                    <motion.div
                                        key={currentStep}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <h2 className="text-2xl lg:text-3xl font-bold text-app-fg mb-3">{currentQuestion.question}</h2>
                                        <p className="text-app-fg-muted mb-8 text-lg">{currentQuestion.description}</p>

                                        <Textarea
                                            value={answers[currentQuestion.id] || ""}
                                            onChange={(e) => setAnswers(prev => ({ ...prev, [currentQuestion.id]: e.target.value }))}
                                            placeholder={currentQuestion.placeholder}
                                            className="min-h-[140px] text-lg bg-app-surface-muted/50 border-app-border focus-visible:ring-brand focus-visible:bg-white resize-none p-5 rounded-2xl shadow-inner"
                                            autoFocus
                                        />

                                        <div className="mt-8 flex justify-end">
                                            <Button
                                                size="lg"
                                                disabled={!canProceed}
                                                onClick={handleNext}
                                                className="bg-brand hover:bg-brand-hover text-white px-8 h-12 rounded-xl text-base shadow-md hover:shadow-lg transition-all hover:scale-105"
                                            >
                                                {isLastStep ? "Concluir e Gerar SWOT" : "Próxima Pergunta"}
                                                {isLastStep ? <SparklesIcon className="w-4 h-4 ml-2" /> : <ChevronRight className="w-5 h-5 ml-2" />}
                                            </Button>
                                        </div>
                                    </motion.div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Loading State */}
                {isLoading && (
                    <div className="bg-white p-12 rounded-3xl shadow-xl border border-app-border-subtle flex flex-col items-center justify-center min-h-[400px] text-center">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                            className="bg-brand-soft p-4 rounded-full mb-6"
                        >
                            <Bot className="w-12 h-12 text-brand" />
                        </motion.div>
                        <h2 className="text-2xl font-bold text-app-fg mb-2">Processando Respostas com I.A...</h2>
                        <p className="text-app-fg-muted mb-8 max-w-md">
                            O Dr. Insights está cruzando suas respostas com as métricas financeiras da sua clínica.
                        </p>
                        <div className="grid grid-cols-2 gap-4 w-full max-w-2xl">
                            <Skeleton className="h-40 w-full rounded-2xl" />
                            <Skeleton className="h-40 w-full rounded-2xl" />
                        </div>
                    </div>
                )}

                {/* Matrix Results */}
                {!isLoading && swotData && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="space-y-8"
                    >
                        <div className="bg-brand-soft border border-brand-soft/50 p-6 rounded-2xl flex items-start flex-col md:flex-row gap-6 shadow-sm">
                            <div className="bg-white p-3 rounded-xl shadow-sm border border-brand-soft shrink-0">
                                <Bot className="w-10 h-10 text-brand" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-brand-hover mb-2">Feedback Estratégico do Dr. Insights</h3>
                                <p className="text-brand-hover/90 leading-relaxed text-lg">
                                    {swotData.ai_message}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Strengths */}
                            <div className="bg-white rounded-2xl shadow-md border hover:border-app-success transition-colors border-app-success/30 overflow-hidden group">
                                <div className="bg-app-success/10 p-5 border-b border-app-success/50 flex items-center gap-3">
                                    <div className="p-2.5 bg-white shadow-sm text-app-success rounded-xl">
                                        <Zap className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-bold text-app-success text-xl tracking-tight">Forças (Strengths)</h3>
                                </div>
                                <ul className="p-6 space-y-5">
                                    {swotData.swot_matrix.strengths.map((item, i) => (
                                        <li key={i} className="flex gap-4 text-app-fg leading-relaxed text-[15px]">
                                            <span className="w-2 h-2 rounded-full bg-app-success mt-2 shrink-0 ring-4 ring-app-success"></span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Weaknesses */}
                            <div className="bg-white rounded-2xl shadow-md border hover:border-app-warning transition-colors border-app-warning/30 overflow-hidden group">
                                <div className="bg-app-warning/10 p-5 border-b border-app-warning/50 flex items-center gap-3">
                                    <div className="p-2.5 bg-white shadow-sm text-app-warning rounded-xl">
                                        <Flag className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-bold text-app-warning text-xl tracking-tight">Fraquezas (Weaknesses)</h3>
                                </div>
                                <ul className="p-6 space-y-5">
                                    {swotData.swot_matrix.weaknesses.map((item, i) => (
                                        <li key={i} className="flex gap-4 text-app-fg leading-relaxed text-[15px]">
                                            <span className="w-2 h-2 rounded-full bg-app-warning mt-2 shrink-0 ring-4 ring-app-warning"></span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Opportunities */}
                            <div className="bg-white rounded-2xl shadow-md border hover:border-app-info transition-colors border-app-info/30 overflow-hidden group">
                                <div className="bg-app-info/10 p-5 border-b border-app-info/50 flex items-center gap-3">
                                    <div className="p-2.5 bg-white shadow-sm text-app-info rounded-xl">
                                        <Target className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-bold text-app-info text-xl tracking-tight">Oportunidades (Opportunities)</h3>
                                </div>
                                <ul className="p-6 space-y-5">
                                    {swotData.swot_matrix.opportunities.map((item, i) => (
                                        <li key={i} className="flex gap-4 text-app-fg leading-relaxed text-[15px]">
                                            <span className="w-2 h-2 rounded-full bg-app-info mt-2 shrink-0 ring-4 ring-app-info"></span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Threats */}
                            <div className="bg-white rounded-2xl shadow-md border hover:border-app-danger transition-colors border-app-danger/30 overflow-hidden group">
                                <div className="bg-app-danger/10 p-5 border-b border-app-danger/50 flex items-center gap-3">
                                    <div className="p-2.5 bg-white shadow-sm text-app-danger rounded-xl">
                                        <ShieldAlert className="w-6 h-6" />
                                    </div>
                                    <h3 className="font-bold text-app-danger text-xl tracking-tight">Ameaças (Threats)</h3>
                                </div>
                                <ul className="p-6 space-y-5">
                                    {swotData.swot_matrix.threats.map((item, i) => (
                                        <li key={i} className="flex gap-4 text-app-fg leading-relaxed text-[15px]">
                                            <span className="w-2 h-2 rounded-full bg-app-danger mt-2 shrink-0 ring-4 ring-app-danger"></span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </motion.div>
                )}

            </motion.div>
        </div>
    );
}

function SparklesIcon(props: any) {
    return (
        <svg
            {...props}
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
            <path d="M5 3v4" />
            <path d="M19 17v4" />
            <path d="M3 5h4" />
            <path d="M17 19h4" />
        </svg>
    )
}
